import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { TrashIcon } from "../../shared/Icons";
import { ReactComponent as InfoIcon } from "../../shared/assets/informationLogo.svg";
import { themeAtom } from "../../shared/atoms";
import { KeyDriverProps } from "../models/AddKeyDriver.model";
import { RowRef } from "../models/DriverInputRow.model";
import { PnlCategory } from "../models/UtilFunctions.model";
import { RouteParams } from "../models/VoyagePnl.model";
import {
  extractCategoriesAndTheirOptions,
  NamingDriverRows,
  toSnakeCase,
  excludedRows,
  PnlContext,
  revenueFields,
  expenseFields,
  formatNumberWithDots,
  canonicalize,
  getDriverComment,
  setDriverComment,
  hasVoyageDriversStoreSnapshot,
  getDriverImpactSumFromStore,
  removeDriverFromStore,
  continueClickedAtomFamily,
  enabledFlagsAtomFamily,
} from "../utils/UtilFunctions";
import DriverInputRow from "./DriverInputRow";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";

const EMPTY_KEY_DRIVERS: ReadonlyArray<PnlCategory> = Object.freeze([] as PnlCategory[]);

const KeyDriver: React.FC<KeyDriverProps> = ({
  driverName,
  driverDefinition,
  rowRefs,
  rowOffset,
  commentResetRefs,
  onClose,
  registerReset,
}) => {
  const theme = useAtomValue(themeAtom);

  const voyagePnlData = useAtomValue(PnlContext);
  const { tcCode, voyageNo, isApprover } = useParams<RouteParams>();
  const { hasFeature } = useFeatureAccess();
  const voyageScope = useMemo(() => ({ tcCode, voyageNo }), [tcCode, voyageNo]);
  const continueClicked = useAtomValue(continueClickedAtomFamily(voyageScope));
  const enabledFlags = useAtomValue(enabledFlagsAtomFamily(voyageScope));
  const isApprovalMode = isApprover === "approval";
  const driverTooltipId = useMemo(() => `voyage-pnl-driver-definition-${canonicalize(driverName)}`, [driverName]);
  const hasStoreSnapshotOnLoad = useMemo(
    () => !isApprovalMode && hasVoyageDriversStoreSnapshot(tcCode, voyageNo),
    [isApprovalMode, tcCode, voyageNo],
  );

  const commentFieldName = `${toSnakeCase(driverName)}-Comments`;

  const [comment, setComment] = useState("");
  const [totalImpact, setTotalImpact] = useState(0);

  const userCanReviewPnl = isApprover === "approval" && hasFeature(FEATURE_KEYS.REVIEW_PNL);

  // Driver object from context (reuse, don't .find twice)

  const existingDriver = useMemo(
    () => voyagePnlData?.addDrivers?.find((d) => canonicalize(d.driver) === canonicalize(driverName)) ?? null,
    [voyagePnlData?.addDrivers, driverName],
  );

  useEffect(() => {
    if (isApprovalMode) {
      setComment(existingDriver?.comments ?? "");
      return;
    }

    // Prefer existing draft snapshot; otherwise hydrate from backend on first load.
    const c = getDriverComment(tcCode, voyageNo, driverName);
    if (hasStoreSnapshotOnLoad) setComment(c);
    else if (c) setComment(c);
    else {
      const backendComment = existingDriver?.comments ?? "";
      setComment(backendComment);

      // Seed backend comments into the local voyage snapshot on first draft load.
      if (backendComment.trim().length > 0) {
        setDriverComment(tcCode, voyageNo, driverName, backendComment);
      }
    }
  }, [isApprovalMode, driverName, existingDriver, hasStoreSnapshotOnLoad, tcCode, voyageNo]);

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setComment(value);
    if (!isApprovalMode) {
      setDriverComment(tcCode, voyageNo, driverName, value);
    }
  };

  // Compute TOTAL impact from store (draft mode) or backend payload (approval mode).
  const updateTotal = useCallback(() => {
    if (isApprovalMode) {
      const revenueSet = new Set(revenueFields.map((v) => v.toLowerCase()));
      const expenseSet = new Set(expenseFields.map((v) => v.toLowerCase()));
      const impacts = existingDriver?.driverCategoryImpact ?? [];

      let revenue = 0;
      let expense = 0;
      impacts.forEach((item) => {
        const impact = typeof item.impact === "number" ? item.impact : 0;
        const category = (item.category ?? "").toLowerCase();
        if (revenueSet.has(category)) revenue += impact;
        else if (expenseSet.has(category)) expense += impact;
      });

      setTotalImpact(revenue - expense);
      return;
    }

    const total = getDriverImpactSumFromStore(tcCode, voyageNo, driverName);
    setTotalImpact(total);
  }, [isApprovalMode, existingDriver?.driverCategoryImpact, tcCode, voyageNo, driverName]);

  useEffect(() => {
    updateTotal();
  }, [updateTotal]);

  // Row ref setter: keep both global & local refs, then update total
  const setRowRef = useCallback(
    (index: number) => (el: RowRef | null) => {
      rowRefs.current[rowOffset + index] = el; // for "Reset All"
      updateTotal();
    },
    [rowRefs, rowOffset, updateTotal],
  );

  // Register comment reset with a snapshot to please eslint/react-hooks
  useEffect(() => {
    const map = commentResetRefs.current;

    const reset = () => {
      if (!isApprovalMode) {
        setDriverComment(tcCode, voyageNo, driverName, "");
      }
      setComment("");
    };

    map[driverName] = reset;

    return () => {
      if (map[driverName] === reset) delete map[driverName];
    };
  }, [driverName, commentResetRefs, tcCode, voyageNo, isApprovalMode]);

  // Register driver-scoped row reset (no global index math)
  useEffect(() => {
    const unregister = registerReset?.(driverName, () => {
      // Reset all rows under this driver
      if (!isApprovalMode) {
        removeDriverFromStore(tcCode, voyageNo, driverName);
        setComment("");
      } else {
        setComment(existingDriver?.comments ?? "");
      }
      updateTotal();
    });
    return () => {
      unregister && unregister();
    };
  }, [driverName, registerReset, updateTotal, tcCode, voyageNo, isApprovalMode, existingDriver?.comments]);

  const rawKeyDrivers = voyagePnlData?.keyDrivers;
  const keyDrivers = useMemo(() => rawKeyDrivers ?? EMPTY_KEY_DRIVERS, [rawKeyDrivers]);

  // Derived lists (memoized)
  const revenueKeyDrivers = useMemo(
    () => keyDrivers.filter((item) => revenueFields.includes(item.category)),
    [keyDrivers],
  );
  const expenseKeyDrivers = useMemo(
    () => keyDrivers.filter((item) => expenseFields.includes(item.category)),
    [keyDrivers],
  );

  const namingRevenueDriverRows = useMemo(
    () => NamingDriverRows(driverName, revenueKeyDrivers, excludedRows),
    [driverName, revenueKeyDrivers],
  );
  const namingExpenseDriverRows = useMemo(
    () => NamingDriverRows(driverName, expenseKeyDrivers, excludedRows),
    [driverName, expenseKeyDrivers],
  );

  const revenueCount = namingRevenueDriverRows.length;

  const extractedCategoriesAndOptions = useMemo(
    () => extractCategoriesAndTheirOptions(keyDrivers as PnlCategory[]),
    [keyDrivers],
  );

  return (
    <table
      className={twMerge(
        (continueClicked && enabledFlags > 3) || (!continueClicked && enabledFlags > 2) ? "border-b" : "",
        "border-x text-right text-[12px]",
        theme.uwhcAssistant.border,
        theme.uwhcAssistant.text,
      )}
    >
      <thead>
        <tr className={twMerge("h-[44px] border-b", theme.uwhcAssistant.border)}>
          <th
            colSpan={3}
            className={twMerge(
              "px-3 text-left", // keep cell left-aligned and vertically centered
            )}
          >
            <span className="flex items-center">
              {!continueClicked && !userCanReviewPnl && (
                <button type="button" onClick={onClose} title="Hide driver" className="-ml-1 mr-2">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              <span className="flex items-center gap-1.5">
                <span className="flex h-4 items-baseline font-bold">{driverName.toUpperCase()}</span>
                {driverDefinition && (
                  <>
                    <span
                      data-tooltip-id={driverTooltipId}
                      data-tooltip-content={driverDefinition}
                      aria-label={`${driverName} definition`}
                      className="inline-flex cursor-help items-center text-[#1F8475]"
                    >
                      <InfoIcon className="h-3.5 w-3.5" />
                    </span>
                    <Tooltip
                      id={driverTooltipId}
                      place="top-start"
                      offset={10}
                      disableStyleInjection={true}
                      className="max-w-[340px] whitespace-normal rounded-xl border border-[#D6DEE8] bg-white px-3 py-2 text-left text-[12px] leading-5 text-[#475569] shadow-[0_12px_30px_rgba(15,23,42,0.12)]"
                      style={{ opacity: 1, zIndex: 60 }}
                    />
                  </>
                )}
              </span>
            </span>
          </th>
        </tr>
        <tr className={twMerge("h-[44px] border-b", theme.uwhcAssistant.border)}>
          <th
            colSpan={3}
            className={twMerge(
              "px-3 text-right", // keep cell left-aligned and vertically centered
            )}
          >
            <span className="flex w-full items-center justify-end text-right">
              <span className={twMerge("flex h-4 items-baseline", theme.voyPnl.lightGreyText)}>{`TOTAL: `} </span>
              <span className="flex h-4 items-baseline justify-end px-2 font-bold">{` ${formatNumberWithDots(totalImpact)}`}</span>
            </span>
          </th>
        </tr>
        <tr
          className={twMerge("h-[44px] border-b text-right", theme.home.tailwind.backgound, theme.uwhcAssistant.border)}
        >
          <th className="h-full w-[140px] px-4">QUANTITY</th>

          {!continueClicked && <th className="h-full w-[120px] px-4">RATE</th>}

          <th className="h-full w-[120px] px-4">IMPACT</th>
        </tr>
      </thead>
      <tbody className="relative">
        <tr className={twMerge("h-[44px] border-b opacity-60", theme.uwhcAssistant.border)}>
          <td colSpan={3} className={twMerge("h-full px-4 text-left font-bold", theme.voyPnl.rowGreenBg)}></td>
        </tr>
        {namingRevenueDriverRows.map((rowProps, index) => (
          <DriverInputRow
            key={`rev-${index}`}
            ref={setRowRef(index)}
            quantityName={rowProps[0]}
            optionName={rowProps[1]}
            rateName={rowProps[2]}
            impactName={rowProps[3]}
            categoriesAndOptions={extractedCategoriesAndOptions}
            onRowChanged={updateTotal}
          />
        ))}
        <tr className={twMerge("h-[44px] border-y opacity-60", theme.uwhcAssistant.border)}>
          <td colSpan={3} className={twMerge("h-full px-4 text-left font-bold", theme.voyPnl.rowRedBg)}></td>
        </tr>
        {namingExpenseDriverRows.map((rowProps, index) => (
          <DriverInputRow
            key={`exp-${index}`}
            ref={setRowRef(revenueCount + index)}
            quantityName={rowProps[0]}
            optionName={rowProps[1]}
            rateName={rowProps[2]}
            impactName={rowProps[3]}
            categoriesAndOptions={extractedCategoriesAndOptions}
            onRowChanged={updateTotal}
          />
        ))}

        <tr className={twMerge("border-t", theme.uwhcAssistant.border, theme.home.tailwind.backgound)}>
          <td colSpan={continueClicked ? 3 : 4} rowSpan={4} className="h-[176px] p-3 text-left align-middle">
            <textarea
              name={commentFieldName}
              placeholder="Add comments..."
              value={comment}
              onChange={handleCommentChange}
              readOnly={continueClicked || userCanReviewPnl}
              className={twMerge(
                "comment-textarea block h-full w-full resize-none overflow-auto rounded-xl border px-2 py-1 text-[12px] focus:outline-none",
                theme.uwhcAssistant.border,
                theme.uwhcAssistant.aiReplyBg,
                theme.vesselDetails.fields,
              )}
            />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default KeyDriver;
